const BRAND = " | HISEA"

// Google 搜索结果里标题约 600px（≈60 字符）后截断显示，但索引读取的是完整标题。
// 这里留到 70：主动截断会真的丢掉关键词（实测 60 会截掉 13.8% 变体的品类词，
// 如 "...Mid-Calf Rain…" 丢了 Boots），而让 Google 自己截只影响显示。
const TITLE_MAX = 70
const DESC_MAX = 155

// 移除末尾的 |,如果存在的话
const removeTrailingPipe = (text: string) => {
  if (text.trim().endsWith("|")) {
    return text.trim().slice(0, -1)
  }

  return text.trim()
}

function capitalizeFirstLetter(word: string) {
  if (word.length === 0) {
    return word
  } // 如果输入是空字符串，直接返回

  return word.charAt(0).toUpperCase() + word.slice(1)
}

// 多词颜色逐词首字母大写："stone washed blue" -> "Stone Washed Blue"
const toTitleCase = (text: string) =>
  text
    .split(" ")
    .filter(Boolean)
    .map(capitalizeFirstLetter)
    .join(" ")

// 剥掉已有的品牌后缀，避免重复拼接
const stripBrand = (text: string) => text.replace(/\s*\|\s*HISEA\s*$/i, "").trim()

// 取出文案里尚未出现的颜色词，避免 "Stone Washed Blue Blue Denim..." 这类重复；
// 按词比对而非子串，避免 "tan" 命中 "Atlantic"
const missingColorWords = (text: string, color: string) => {
  const tokens = new Set(text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean))

  return color
    .split(" ")
    .filter(Boolean)
    .filter((word) => !tokens.has(word.toLowerCase()))
    .join(" ")
}

// 后台模板把 [product_price] 拼进了 meta title，空间不足时优先牺牲价格而非关键词
const stripPrice = (text: string) =>
  text.replace(/\s*\$\d[\d,]*(\.\d+)?/g, "").replace(/\s{2,}/g, " ").trim()

// 截断优先落在句末，其次落在词边界，避免把句子或单词切断
const clamp = (text: string, max: number) => {
  if (text.length <= max) {
    return text
  }

  const cut = text.slice(0, max - 1)

  const lastStop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "))

  if (lastStop > max * 0.6) {
    return cut.slice(0, lastStop + 1)
  }

  const lastSpace = cut.lastIndexOf(" ")
  const safe = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut

  return safe.replace(/[\s,.;:–-]+$/, "") + "…"
}

export const getProductFullDetailSeo = ({
  meta_title,
  meta_description,
  meta_keyword,
  defaultColorLabel,
}: {
    meta_title: string
    meta_description: string
    meta_keyword: string
    defaultColorLabel: string
}) => {
  const color = defaultColorLabel ? toTitleCase(removeTrailingPipe(defaultColorLabel)) : ""

  // title：颜色前置以匹配 "yellow rain boots" 这类查询，拼装完成后再统一截断
  let title = stripBrand(removeTrailingPipe(meta_title || "").replace(/&#039;/g, "'"))

  if (title) {
    const titleColor = color ? missingColorWords(title, color) : ""

    if (titleColor) {
      title = `${titleColor} ${title}`
    }

    const titleRoom = TITLE_MAX - BRAND.length

    // 放不下时先去掉价格，还放不下才截断——保住颜色和品类关键词
    if (title.length > titleRoom) {
      title = stripPrice(title)
    }

    title = clamp(title, titleRoom) + BRAND
  }

  // description：用分隔符衔接颜色，避免 "Yellow Designed for..." 这类病句；不加品牌后缀
  let description = stripBrand(removeTrailingPipe(meta_description || ""))

  if (description) {
    const descColor = color ? missingColorWords(description, color) : ""

    if (descColor) {
      description = `${descColor} – ${description}`
    }

    description = clamp(description, DESC_MAX)
  }

  // keywords：Google 早已忽略该字段，保持原有行为不动
  let keywords = removeTrailingPipe(meta_keyword || "")

  if (!keywords.toLowerCase().includes(BRAND.toLowerCase())) {
    keywords += BRAND
  }

  if (color) {
    keywords = `${color} ${keywords}`
  }

  return {
    description, keywords, title 
  }
}

export const getNewUrlKey = ({
  colorLabel,
  originalUrlKey,
  searchParamsString,
}: {
    colorLabel: string
    originalUrlKey: string
    searchParamsString?: string
}) => {
  if (!colorLabel) {
    const nextPathname = `${originalUrlKey}.html`

    return `/${nextPathname}${searchParamsString ? `?${searchParamsString}` : ""}`
  }

  const urlKeyParts = originalUrlKey.split("-")

  const newUrlKeyParts = [...urlKeyParts, ...colorLabel.split(" ")]

  const nextPathname = `${newUrlKeyParts.join("-")}.html`

  return `/${nextPathname}${searchParamsString ? `?${searchParamsString}` : ""}`
}

export const getTitle = ({
  nextState,
  key,
}: {
    key: string
    nextState: any
}) => {
  return nextState?.get(key)?.values()?.next()?.value?.title
}

export const getH1Title = ({
  colorTitle,
  lw_size_shoes_adult_title,
  lw_product_usage_title,
  lw_material_title,
  categoryName,
}: {
    colorTitle: string
    lw_size_shoes_adult_title: string
    lw_product_usage_title: string
    lw_material_title: string
    categoryName: string
}) => {
  const titleParts: string[] = []

  const sizeParts: string[] = []

  const category = categoryName?.split(" ")

  const first = category?.[0] // First element
  const others = category?.slice(1) // Remaining elements

  if (first) {
    titleParts.push(first)
  }

  if (lw_product_usage_title) {
    titleParts.push(lw_product_usage_title)
  }

  if (colorTitle) {
    titleParts.push(colorTitle)
  }

  if (lw_material_title) {
    titleParts.push(lw_material_title)
  }

  if (lw_size_shoes_adult_title) {
    sizeParts.push(lw_size_shoes_adult_title)
  }

  const h1title = others
    ? [...titleParts, ...others, ...sizeParts].join(" ")
    : [...titleParts, ...sizeParts].join(" ")

  return h1title
}
